/**
 * Small pieces the PF-3a screens (anomalies, digs, welds, reports) share.
 *
 * NO MODALS. A confirmation is drawn inline where the action was
 * (`PfInlineConfirm`), and a refusal is drawn inline in the refusal style
 * (`PfFormError`: red wash, role="alert") — never in a toast and never in a
 * success colour.
 */
import React from 'react';
import { PF_PRIMARY, PF_SECONDARY, PfFormError } from '../PfBits';

export function PfInlineConfirm({
  message,
  confirmLabel,
  cancelLabel = 'Not now',
  pending,
  error,
  onConfirm,
  onCancel,
  testId,
  children,
}: {
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  pending?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  testId?: string;
  children?: React.ReactNode;
}) {
  return (
    <div data-testid={testId} className="space-y-3 rounded-fi-control border border-fi-amber/50 bg-fieldiq-card px-4 py-3">
      <PfFormError message={error ?? null} />
      <p className="text-sm text-fieldiq-text">{message}</p>
      {children}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
          {cancelLabel}
        </button>
        <button type="button" className={PF_PRIMARY} onClick={onConfirm} disabled={pending}>
          {pending ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}

/** A neutral note (not a refusal, not a success): what the screen is waiting on. */
export function PfNote({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return (
    <p data-testid={testId} className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
      {children}
    </p>
  );
}

/** The form grid every PF-3a form uses. */
export function PfFormGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{children}</div>;
}

/** `label (unit)` — no brackets when the unit is unknown. */
export const unitLabel = (label: string, unit: string | null | undefined) => (unit ? `${label} (${unit})` : label);
